'use client'

import HeaderFinal from '../components/HeaderFinal'
import Footer from '../components/Footer'
import WhatsAppFloatingButton from '../components/WhatsAppFloatingButton'
import VideoBackground from "../components/VideoBackground"

// =================== TIPOS Y COMPONENTE HEXÁGONO ===================
type HexOption = {
  id: number | string
  title?: string
  price?: string
  subtitle?: string
  href?: string
  featured?: boolean
}

const HexagonOption = ({ option, size = 'md', color = 'default' }: { option: HexOption; size?: 'sm' | 'md' | 'lg'; color?: 'default' | 'cyan' | 'pink' | 'yellow' | 'ghost' | 'blue' | 'orange' | 'fuchsia' }) => {
  const textClasses = {
    sm: { text: 'text-[10px]', price: 'text-sm'  },
    md: { text: 'text-base',   price: 'text-lg'  },
    lg: { text: 'text-xl',     price: 'text-2xl' },
  }[size];

  const polygonStyle = {
    default: 'fill-white/10 stroke-white/30 stroke-[2px] stroke-dashed',
    cyan:    'fill-cyan-500/50 stroke-cyan-300 stroke-[3px]',
    pink:    'fill-pink-500/50 stroke-pink-300 stroke-[3px]',
    yellow:  'fill-yellow-500/50 stroke-yellow-300 stroke-[3px]',
    ghost:   'fill-transparent stroke-gray-400/60 stroke-[2px]',
    blue:    'fill-blue-500/40 stroke-blue-300 stroke-[3px]',
    orange:  'fill-orange-500/40 stroke-orange-300 stroke-[3px]',
    fuchsia: 'fill-fuchsia-500/40 stroke-fuchsia-300 stroke-[3px]',
  }[color];

  return (
    <div className="relative w-full h-full">
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 115" xmlns="http://www.w3.org/2000/svg">
        <polygon points="50,0 100,28.75 100,86.25 50,115 0,86.25 0,28.75" className={polygonStyle} />
      </svg>
      {option.title && (
        <div className="relative z-10 flex flex-col items-center justify-center h-full p-3 text-center">
          <h3 className={`font-bold font-bisteron text-white leading-tight drop-shadow-[0_0_3px_rgba(0,0,0,0.9)] ${textClasses.text}`}>{option.title}</h3>
          {option.price && (
            <p className={`mt-1 font-bold font-sans drop-shadow-[0_0_3px_rgba(0,0,0,0.9)] ${textClasses.price} ${option.featured ? 'text-pink-300' : 'text-white'}`}>{option.price}</p>
          )}
          {option.subtitle && (
            <p className="text-[10px] text-gray-300 mt-1 drop-shadow-[0_0_3px_rgba(0,0,0,0.9)]">{option.subtitle}</p>
          )}
          {option.featured && (
            <p className="text-[10px] text-pink-300 mt-1 drop-shadow-[0_0_3px_rgba(0,0,0,0.9)]">★ Más popular</p>
          )}
        </div>
      )}
    </div>
  );
};

// =================== DATOS PAQUETES ANIVERSARIO ===================
const packagesAniversario: HexOption[] = [
  { id: 'basico',  title: 'BÁSICO',  price: '$640k', subtitle: 'En pareja' },
  { id: 'premium', title: 'PREMIUM', price: '$765k', subtitle: 'Con mesa decorada', featured: true },
  { id: 'pro',     title: 'PRO',     price: '$965k', subtitle: 'Con acrobacias' },
];

const ghost = (id: string): HexOption => ({ id: `ghost-${id}` });

// =================== PANAL DE 3 HEXES PRINCIPALES ===================
const PanalConPaquetes = () => (
  <div className="relative mx-auto w-full max-w-[900px] aspect-[900/900]">
    <div className="absolute z-30 hex-breathe hex-breathe-1" style={{ top: '-10.93%', left: '26.00%', width: '48%', aspectRatio: '100/115' }}><HexagonOption option={packagesAniversario[1]} size="lg" color="orange" /></div>
    <div className="absolute z-30 hex-breathe hex-breathe-2" style={{ top: '39.07%', left: '-2.86%', width: '48%', aspectRatio: '100/115' }}><HexagonOption option={packagesAniversario[0]} size="lg" color="blue" /></div>
    <div className="absolute z-30 hex-breathe hex-breathe-3" style={{ top: '39.07%', left: '54.86%', width: '48%', aspectRatio: '100/115' }}><HexagonOption option={packagesAniversario[2]} size="lg" color="fuchsia" /></div>
  </div>
);

// =================== LLUVIA FULLSCREEN ===================
const FullscreenRain = () => (
  <div className="rain-fullscreen">
    <div className="rain-1" style={{ left: '3%' }}><HexagonOption option={ghost('R1')} color="ghost" size="md" /></div>
    <div className="rain-2" style={{ left: '14%' }}><HexagonOption option={ghost('R2')} color="ghost" size="md" /></div>
    <div className="rain-3" style={{ left: '24%' }}><HexagonOption option={ghost('R3')} color="ghost" size="md" /></div>
    <div className="rain-4" style={{ left: '37%' }}><HexagonOption option={ghost('R4')} color="ghost" size="md" /></div>
    <div className="rain-5" style={{ left: '48%' }}><HexagonOption option={ghost('R5')} color="ghost" size="md" /></div>
    <div className="rain-6" style={{ left: '60%' }}><HexagonOption option={ghost('R6')} color="ghost" size="md" /></div>
    <div className="rain-7" style={{ left: '71%' }}><HexagonOption option={ghost('R7')} color="ghost" size="md" /></div>
    <div className="rain-8" style={{ left: '83%' }}><HexagonOption option={ghost('R8')} color="ghost" size="md" /></div>
    <div className="rain-9" style={{ left: '92%' }}><HexagonOption option={ghost('R9')} color="ghost" size="md" /></div>
    <div className="rain-10" style={{ left: '10%' }}><HexagonOption option={ghost('R10')} color="ghost" size="md" /></div>
    <div className="rain-11" style={{ left: '38%' }}><HexagonOption option={ghost('R11')} color="ghost" size="md" /></div>
    <div className="rain-12" style={{ left: '66%' }}><HexagonOption option={ghost('R12')} color="ghost" size="md" /></div>
    <div className="rain-13" style={{ left: '20%' }}><HexagonOption option={ghost('R13')} color="ghost" size="md" /></div>
    <div className="rain-14" style={{ left: '50%' }}><HexagonOption option={ghost('R14')} color="ghost" size="md" /></div>
    <div className="rain-15" style={{ left: '80%' }}><HexagonOption option={ghost('R15')} color="ghost" size="md" /></div>
  </div>
);

export default function AniversariosPage() {
  const anniversaryPackages = [
    {
      name: 'PAQUETE ANIVERSARIO BÁSICO',
      price: '$640,000',
      description: 'Sorpresa romántica para 2 personas',
      features: [
        '2 Vuelos Recreativos Max (Ambos vuelan al tiempo)',
        'Cada uno en su propio parapente',
        'Incluye vídeo GoPro',
        'Aviso en tierra personalizado',
        'Instructores certificados FEDEAEREOS',
        'Póliza de asistencia médica para ambos',
        'Equipo completo de parapente para ambos'
      ],
      color: 'pink',
      borderColor: 'border-pink-500/40',
      textColor: 'text-pink-700',
      popular: true,
      whatsappMessage: '¡Hola!%20Quiero%20reservar%20el%20PAQUETE%20ANIVERSARIO%20BÁSICO%20de%20Airlovers%20($640,000)%20para%20celebrar%20un%20aniversario%20en%20pareja'
    },
    {
      name: 'PAQUETE ANIVERSARIO PREMIUM',
      price: '$765,000',
      description: 'Celebración completa para 2 personas',
      features: [
        '2 Vuelos Recreativos Max (Ambos vuelan al tiempo)',
        'Cada uno en su propio parapente',
        'Incluye vídeo GoPro',
        'Aviso en tierra personalizado',
        'Instructores certificados FEDEAEREOS',
        'Póliza de asistencia médica para ambos',
        'Equipo completo de parapente para ambos',
        'Mesa con decoración especial para aniversario',
        'Almuerzo para 2 (según menú y bebida incluida)'
      ],
      color: 'red',
      borderColor: 'border-red-500/40',
      textColor: 'text-red-700',
      popular: false,
      whatsappMessage: '¡Hola!%20Quiero%20reservar%20el%20PAQUETE%20ANIVERSARIO%20PREMIUM%20de%20Airlovers%20($765,000)%20para%20un%20aniversario%20especial%20en%20pareja'
    },
    {
      name: 'PAQUETE ANIVERSARIO PRO',
      price: '$965,000',
      description: 'Experiencia extrema para 2 personas',
      features: [
        '2 Vuelos Recreativos Pro Max (acrobacias)',
        'Todo del Paquete Premium +',
        'Acrobacias aéreas (Delfines, Wing overs)',
        '2 Cámaras profesionales con tomas extremas',
        'Instructor especialista en acrobacias',
        'Experiencia de adrenalina compartida',
        'Aviso en tierra sorpresa',
        'Mesa decorada',
        'Almuerzo a la carta con bebida para ambos según menú'
      ],
      color: 'purple',
      borderColor: 'border-purple-500/40',
      textColor: 'text-purple-700',
      popular: false,
      whatsappMessage: '¡Hola!%20Quiero%20reservar%20el%20PAQUETE%20ANIVERSARIO%20PRO%20de%20Airlovers%20($965,000)%20para%20un%20aniversario%20inolvidable%20en%20pareja'
    },
    {
      name: 'PLAN CACIQUE ENTRE SEMANA',
      price: '$860,000',
      description: 'Aventura completa de lunes a jueves',
      features: [
        '2 vuelos clásicos con fotos y video incluido',
        '1 Noche de alojamiento en Glamping',
        'Vista espectacular a la represa',
        '2 copas de vino para brindar',
        'Desayuno para 2 incluido',
        'Noche de fogata privada',
        'Madera y masmelos para asar'
      ],
      color: 'amber',
      borderColor: 'border-amber-500/40',
      textColor: 'text-amber-700',
      popular: true,
      whatsappMessage: '¡Hola!%20Quiero%20reservar%20el%20PLAN%20CACIQUE%20ENTRE%20SEMANA%20de%20Airlovers%20($860,000)%20para%20un%20aniversario%20especial',
      note: 'Válido domingo a jueves • Incluye alojamiento'
    },
    {
      name: 'PLAN CACIQUE FIN DE SEMANA',
      price: '$1,020,000',
      description: 'Escape romántico de fin de semana',
      features: [
        '2 vuelos clásicos con fotos y video incluido',
        '1 Noche de alojamiento en Glamping',
        'Vista panorámica a la represa',
        '2 copas de vino para brindar',
        'Desayuno para 2 incluido',
        'Noche de fogata privada',
        'Experiencia completa de pareja'
      ],
      color: 'purple',
      borderColor: 'border-purple-500/40',
      textColor: 'text-purple-700',
      popular: false,
      whatsappMessage: '¡Hola!%20Quiero%20reservar%20el%20PLAN%20CACIQUE%20FIN%20DE%20SEMANA%20de%20Airlovers%20($1,020,000)%20para%20un%20aniversario%20inolvidable',
      note: 'Válido viernes, sábados y días antes de festivo'
    }
  ]

  const colorClasses = {
    pink: 'bg-pink-500/10',
    purple: 'bg-purple-500/10',
    red: 'bg-red-500/10',
    amber: 'bg-amber-500/10'
  }

  const buttonColorClasses = {
    pink: 'bg-pink-600 hover:bg-pink-700',
    red: 'bg-red-600 hover:bg-red-700',
    purple: 'bg-purple-600 hover:bg-purple-700',
    amber: 'bg-amber-600 hover:bg-amber-700'
  }

  return (
    <>
      <style jsx global>{`
        /* Aniversarios rain-fullscreen */
        .rain-fullscreen {
          position: fixed;
          inset: 0;
          z-index: 30;
          pointer-events: none;
          overflow: hidden;
        }
        .rain-fullscreen > div {
          position: absolute;
          top: 0;
          will-change: transform;
          animation-iteration-count: infinite;
          animation-timing-function: linear;
        }
        .rain-1, .rain-2, .rain-3, .rain-4, .rain-5, .rain-6, .rain-7, .rain-8, .rain-9 {
          width: 10vw; max-width: 180px; aspect-ratio: 100 / 115;
        }
        .rain-3, .rain-7 { width: 12vw; max-width: 220px; }
        .rain-2, .rain-4, .rain-6, .rain-8 { width: 8vw;  max-width: 140px; }
        .rain-10, .rain-11, .rain-12, .rain-13, .rain-14, .rain-15 {
          width: 14vw; max-width: 260px; aspect-ratio: 100 / 115;
        }
        .rain-11, .rain-14 { width: 16vw; max-width: 290px; }
        .rain-10, .rain-13 { width: 18vw; max-width: 320px; }
        .rain-2, .rain-4, .rain-6, .rain-8, .rain-11, .rain-12, .rain-15 { animation-name: rain-right; }
        .rain-1, .rain-3, .rain-5, .rain-7, .rain-9, .rain-10, .rain-13, .rain-14 { animation-name: rain-left; }
        .rain-2  { animation-duration: 14s; animation-delay: -2s; }
        .rain-4  { animation-duration: 15s; animation-delay: -1s; }
        .rain-6  { animation-duration: 13s; animation-delay: -3s; }
        .rain-8  { animation-duration: 10s; animation-delay: -4s; }
        .rain-11 { animation-duration: 25s; animation-delay: -8s; }
        .rain-12 { animation-duration: 28s; animation-delay: -3s; }
        .rain-15 { animation-duration: 22s; animation-delay: -12s; }
        .rain-1  { animation-duration: 12s; animation-delay:  0s; }
        .rain-3  { animation-duration:  9s; animation-delay: -5s; }
        .rain-5  { animation-duration: 11s; animation-delay: -7s; }
        .rain-7  { animation-duration: 16s; animation-delay: -8s; }
        .rain-9  { animation-duration: 17s; animation-delay: -6s; }
        .rain-10 { animation-duration: 30s; animation-delay: -5s; }
        .rain-13 { animation-duration: 26s; animation-delay: -15s; }
        .rain-14 { animation-duration: 24s; animation-delay: -10s; }
        @media (max-width: 768px) {
          .rain-fullscreen .rain-1 polygon, .rain-fullscreen .rain-2 polygon,
          .rain-fullscreen .rain-3 polygon, .rain-fullscreen .rain-4 polygon,
          .rain-fullscreen .rain-5 polygon, .rain-fullscreen .rain-6 polygon,
          .rain-fullscreen .rain-7 polygon, .rain-fullscreen .rain-8 polygon,
          .rain-fullscreen .rain-9 polygon { stroke-width: 8px; }
          .rain-fullscreen .rain-10 polygon, .rain-fullscreen .rain-11 polygon,
          .rain-fullscreen .rain-12 polygon, .rain-fullscreen .rain-13 polygon,
          .rain-fullscreen .rain-14 polygon, .rain-fullscreen .rain-15 polygon { stroke-width: 5px; }
        }
        @keyframes rain-right {
          0%   { transform: translate(-100vw, -10vh); }
          100% { transform: translate(100vw, 110vh); }
        }
        @keyframes rain-left {
          0%   { transform: translate(100vw, -10vh); }
          100% { transform: translate(-100vw, 110vh); }
        }
        .hex-breathe {
          transform-origin: center center;
          will-change: transform;
          animation-iteration-count: infinite;
        }
        .hex-breathe-1 { animation-name: breathe; animation-duration: 3.0s; animation-delay: 0s;    }
        .hex-breathe-2 { animation-name: breathe; animation-duration: 3.0s; animation-delay: -1.0s; }
        .hex-breathe-3 { animation-name: breathe; animation-duration: 3.0s; animation-delay: -2.0s; }
        @keyframes breathe {
          0%      { transform: scale(1.00); animation-timing-function: linear; }
          66.67%  { transform: scale(1.00); animation-timing-function: cubic-bezier(0.4, 0, 0.6, 1); }
          83.33%  { transform: scale(1.08); animation-timing-function: cubic-bezier(0.4, 0, 0.6, 1); }
          100%    { transform: scale(1.00); }
        }
        @media (prefers-reduced-motion: reduce) {
          .hex-breathe { animation: none !important; }
          .rain-fullscreen > div { animation: none !important; }
        }
      `}</style>
      <HeaderFinal />

      {/* LLUVIA HEXAGONAL FULLSCREEN */}
      <FullscreenRain />

      <main className="min-h-screen bg-black text-white">
        {/* Hero Section con video de fondo */}
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
          {/* Video de fondo - FIJADO */}
          <VideoBackground 
            videoSrc="/videos/video_aniversarios_optimized_v2.mp4"
            overlayColor=""  // Sin overlay para colores vivos
            fixed={true}
          />

          {/* Contenido sobre video */}
          <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold mb-6 font-bisteron tracking-wider px-4 overflow-visible drop-shadow-[0_0_2px_rgba(255,255,255,1),0_0_4px_rgba(255,255,255,1),0_0_6px_rgba(255,255,255,0.9),0_0_8px_rgba(255,255,255,0.8),0_0_10px_rgba(255,255,255,0.7),0_0_12px_rgba(255,255,255,0.6),0_0_14px_rgba(255,255,255,0.5)]">
              <div className="whitespace-nowrap">ANIVERSARIO</div>
              <div className="text-pink-400">POR LO ALTO</div>
            </h1>
            
            <p className="text-xl md:text-2xl text-white/95 max-w-3xl mx-auto mb-10 font-light">
              Celebra tu aniversario volando sobre el Embalse Tominé. 
              Paquetes desde $640,000 COP para 2 personas con vuelo, video y celebración especial.
            </p>
            
            <div className="inline-flex items-center bg-white/10 rounded-full px-8 py-4 border border-white/20">
              <span className="text-pink-400 font-bold mr-3">💖</span>
              <span className="text-white font-bisteron tracking-wider">
                <strong>EXPERIENCIA ROMÁNTICA</strong> - RESERVA HOY MISMO
              </span>
            </div>
          </div>

          {/* Flecha indicadora */}
          <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
            <svg className="w-8 h-8 text-white/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </div>
        </section>

        {/* Sección de paquetes - Solo los paquetes */}
        <section className="py-20 bg-gradient-to-b from-black via-black/95 to-black">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            {/* Grid de paquetes - RECUADROS TRANSPARENTES */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
              {anniversaryPackages.map((pkg, index) => (
                <div 
                  key={index}
                  className={`rounded-[2rem] border-3 p-8 transition-all duration-500 
                    hover:scale-105 hover:border-white/60
                    ${colorClasses[pkg.color as keyof typeof colorClasses]} 
                    ${pkg.borderColor} ${pkg.textColor}`}
                  style={{
                    transformStyle: 'preserve-3d',
                    perspective: '1000px'
                  }}
                >
                  {pkg.popular && pkg.name !== 'PLAN CACIQUE ENTRE SEMANA' && (
                    <div className="inline-block bg-gradient-to-r from-pink-600 to-purple-600 text-white px-6 py-2 rounded-full text-sm font-bold mb-6 font-bisteron tracking-wider">
                      🎉 MÁS POPULAR
                    </div>
                  )}
                  {pkg.name === 'PLAN CACIQUE ENTRE SEMANA' && (
                    <div className="inline-block bg-gradient-to-r from-amber-600 to-orange-600 text-white px-6 py-2 rounded-full text-sm font-bold mb-6 font-bisteron tracking-wider">
                      ⭐ RECOMENDADO
                    </div>
                  )}
                  
                  {/* Título con fondo blanco y glow */}
                  <h3 className="text-2xl font-bold mb-4 uppercase tracking-wider font-bisteron bg-white/90 px-6 py-3 rounded-xl mb-6 drop-shadow-[0_0_2px_rgba(255,255,255,1),0_0_4px_rgba(255,255,255,1),0_0_6px_rgba(255,255,255,0.9),0_0_8px_rgba(255,255,255,0.8)]">
                    {pkg.name}
                  </h3>
                  
                  {/* Precio - CON ÓVALO BLANCO DE FONDO */}
                  <div className="relative mb-6">
                    {/* Óvalo blanco de fondo */}
                    <div className="absolute inset-0 bg-white/90 rounded-full blur-sm scale-110"></div>
                    
                    {/* Contenedor del precio */}
                    <div className="relative z-10 text-4xl font-bold font-sans px-8 py-4 [text-shadow:_0_0_4px_rgba(0,0,0,0.8),0_0_8px_rgba(0,0,0,0.6),0_0_12px_rgba(0,0,0,0.4)]">
                      {pkg.price} <span className="text-lg [text-shadow:_0_0_2px_rgba(0,0,0,0.8),0_0_4px_rgba(0,0,0,0.6),0_0_6px_rgba(0,0,0,0.4)]">COP</span>
                    </div>
                  </div>
                  
                  {/* Descripción */}
                  <p className="text-white/90 mb-8 text-lg">{pkg.description}</p>
                  
                  {/* Línea divisoria con glow */}
                  <div className="w-full h-px bg-gradient-to-r from-transparent via-current to-transparent mb-8 shadow-[0_0_2px_rgba(255,255,255,0.5),0_0_4px_rgba(255,255,255,0.4)]"></div>
                  
                  {/* Características */}
                  <div className="mb-10">
                    <h4 className="font-bold text-xl mb-6 text-white font-bisteron tracking-wider">INCLUYE:</h4>
                    <ul className="space-y-4">
                      {pkg.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start">
                          <span className="text-pink-400 text-xl mr-3 mt-1">✓</span>
                          <span className="text-white/90 text-lg">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    {pkg.note && (
                      <div className="mt-6 p-4 bg-white/10 rounded-xl border border-white/20">
                        <p className="text-white/80 text-sm italic">📌 {pkg.note}</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Botón de reserva */}
                  <a
                    href={`https://wa.me/573028309637?text=${pkg.whatsappMessage}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`block w-full text-center text-white font-bold py-4 px-6 rounded-xl 
                      transition-all duration-300 hover:scale-105 font-bisteron tracking-wider text-lg
                      ${buttonColorClasses[pkg.color as keyof typeof buttonColorClasses]}`}
                  >
                    RESERVAR ESTE PAQUETE
                  </a>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <WhatsAppFloatingButton />
    </>
  )
}